import { t } from "@/i18n";

/**
 * Determina si una etiqueta o nombre de archivo es técnico o generado automáticamente.
 * Reconoce únicamente formatos automáticos concretos:
 * - Nombres vacíos, nulos o no-strings
 * - Prefijos automáticos concretos: gen_, ast_, upload_, up_, job_, tmp_, node_
 * - Formato UUID (con o sin extensión)
 * - Hashes hexadecimales o IDs de MongoDB de 24+ caracteres
 * - Marcadores exactos de activo sin título ("untitled", "sin título", "sin titulo")
 *
 * Palabras descriptivas como "demo", "resultado" o "upscale" dentro de un título
 * NO son técnicas y se conservan como nombres descriptivos reales.
 */
export function isTechnicalName(name) {
  if (!name || typeof name !== "string") return true;
  const trimmed = name.trim();
  if (!trimmed) return true;

  const lower = trimmed.toLowerCase();

  // Marcadores exactos de activo sin título
  if (lower === "untitled" || lower === "sin título" || lower === "sin titulo") {
    return true;
  }

  // 1. Prefijos automáticos concretos del sistema (gen_, ast_, upload_, up_, job_, tmp_, node_, sim_)
  if (/^(gen_|ast_|upload_|up_|job_|tmp_|node_|sim_)/i.test(trimmed)) {
    return true;
  }

  // 2. Formato UUID estándar (en cualquier posición del nombre técnico)
  if (/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(trimmed)) {
    return true;
  }

  // 3. Hashes hexadecimales o IDs de MongoDB de 24+ caracteres
  if (/^[0-9a-f]{24,}(\.[a-zA-Z0-9]+)?$/i.test(trimmed)) {
    return true;
  }

  return false;
}

/**
 * Obtiene el nombre técnico o de archivo original para tooltip o referencia.
 */
export function getTechnicalFilename(asset) {
  if (!asset) return "";
  if (asset.filename) return asset.filename;
  if (isTechnicalName(asset.label)) return asset.label;
  if (isTechnicalName(asset.name)) return asset.name;
  if (asset.url) {
    try {
      const cleanUrl = asset.url.split("?")[0];
      const last = cleanUrl.substring(cleanUrl.lastIndexOf("/") + 1);
      if (last && (last.includes(".") || isTechnicalName(last))) {
        return last;
      }
    } catch {
      // fallback
    }
  }
  return asset.label || asset.name || asset.id || "";
}

/**
 * Resuelve el nombre legible para un activo según los criterios:
 * 1. Si el asset tiene un nombre descriptivo real, lo muestra (incluyendo nombres con "demo", "resultado", etc.).
 * 2. Si su nombre es técnico o generado automáticamente, utiliza el título de la escena y el tipo de medio (Ej: Escena 01 — Prueba · Vídeo).
 * 3. Si tampoco hay título de escena: Vídeo sin título / Imagen sin título.
 */
export function getReadableAssetTitle({ asset, scene, t: translations }) {
  if (!asset) return "";
  const tr = translations || t;
  const isVideo = asset.type === "video";
  const mediaType = isVideo
    ? (tr.editor?.videoType || "Vídeo")
    : (tr.editor?.imageType || "Imagen");

  // 1. Si el asset tiene un nombre descriptivo real, muéstralo
  const candidateName = asset.label || asset.name || "";
  if (candidateName && !isTechnicalName(candidateName)) {
    return candidateName;
  }

  // 2. Si su nombre es técnico o generado automáticamente, utiliza el título de la escena y el tipo de medio
  const sceneTitle = scene?.title?.trim() || scene?.name?.trim();
  if (sceneTitle) {
    return `${sceneTitle} · ${mediaType}`;
  }

  // 3. Si tampoco hay título de escena: Vídeo sin título / Imagen sin título
  if (isVideo) {
    return tr.editor?.untitledVideo || "Vídeo sin título";
  }
  return tr.editor?.untitledImage || "Imagen sin título";
}

/**
 * Formatea el título de un elemento evitando duplicar el tipo de medio si ya está presente.
 * Evita repeticiones como "Vídeo (Vídeo)" o "Escena 01 · Vídeo (Vídeo)".
 */
export function formatItemTitle(name, fileType) {
  if (!name) return fileType || "";
  if (!fileType) return name;
  const lowerName = name.toLowerCase();
  const lowerType = fileType.toLowerCase();
  if (
    lowerName.endsWith(`· ${lowerType}`) ||
    lowerName.endsWith(lowerType) ||
    lowerName.includes(lowerType)
  ) {
    return name;
  }
  return `${name} (${fileType})`;
}

/**
 * Devuelve el texto simplificado del tooltip para el archivo original:
 * "Archivo original: nombre_tecnico.mp4"
 */
export function getOriginalFileTooltip({ asset, t: translations }) {
  const tr = translations || t;
  const technical = getTechnicalFilename(asset);
  const prefix = tr.editor?.originalFile || "Archivo original";
  return technical ? `${prefix}: ${technical}` : "";
}
